import React, { useState } from 'react';
import { SearchUniversal } from '@duxton-web/icons';
import {
  Card,
  ConfigProvider,
  Input,
  Pagination,
  Table,
  TablePaginationConfig,
  Tooltip,
  Typography,
} from '@duxton-web/ui';

import { useVariableDispatchContext } from '@/features/variable/contexts/variable-context';
import CreateEntity from '@/features/variable/settings/product-entity/create-entity';
import DeleteEntity from '@/features/variable/settings/product-entity/delete-entity';
import { updateVariableProductEntity } from '@/features/variable/store/actions/variable/action-creators';
import { useGetProductEntities } from '@/hooks';
import { isAdmin } from '@/utils/user';

const PAGE_SIZE = 10;

const Entities = ({
  currentPage,
  data,
  isLoading,
  onPageChange,
  onDeleteClick = () => {},
  onCancel = () => {},
  onOk = () => {},
  onItemSelect = () => {},
}: {
  currentPage: number;
  data: any;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onDeleteClick?: () => void;
  onCancel?: () => void;
  onOk?: () => void;
  onItemSelect?: () => void;
}) => {
  const variableDispatch = useVariableDispatchContext();

  const columns: any[] = [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Name',
      width: 580,
      render: (value, _) => {
        return (
          <div
            role="button"
            style={{
              cursor: 'pointer',
            }}
            onClick={(_) => {
              variableDispatch(updateVariableProductEntity(value));

              onItemSelect();
            }}
          >
            <Tooltip title={value}>
              <Typography.Text ellipsis style={{ maxWidth: '580px' }}>
                {value}
              </Typography.Text>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  if (isAdmin())
    columns.push({
      title: '',
      width: 75,
      align: 'center',
      render: (_, record) => {
        if (!record?.variableCount)
          return (
            <DeleteEntity
              productEntityName={record?.name}
              count={record?.variableCount}
              onDeleteClick={onDeleteClick}
              onCancel={onCancel}
              onOk={onOk}
            />
          );

        return (
          <Tooltip title="This product entity is used by one or more variables hence can't be deleted">
            <DeleteEntity
              productEntityName={record?.name}
              count={record?.variableCount}
              onDeleteClick={onDeleteClick}
              onCancel={onCancel}
              onOk={onOk}
            />
          </Tooltip>
        );
      },
    });

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            footerBg: 'white',
          },
        },
      }}
    >
      <Table
        data-testid="entities-list-product-names"
        loading={isLoading}
        dataSource={data?.data?.items}
        size="middle"
        columns={columns}
        onChange={({ current }: TablePaginationConfig) => {
          onPageChange(current || 1);
        }}
        pagination={false}
        footer={(_) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                size="small"
                showSizeChanger={false}
                style={{
                  backgroundColor: 'white',
                }}
                current={currentPage}
                total={data?.data?.total || 0}
                pageSize={PAGE_SIZE}
                onChange={(page, _) => {
                  onPageChange(page);
                }}
              />
            </div>
          );
        }}
      />
    </ConfigProvider>
  );
};

const EntitiesListTitle = ({
  isLoading,
  onSearch,
  onCreateClick = () => {},
  onOk = () => {},
  onCancel = () => {},
}: {
  isLoading: boolean;
  onSearch: (value: string) => void;
  onCreateClick?: () => void;
  onOk?: () => void;
  onCancel?: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Input.Search
        disabled={isLoading}
        enterButton={<SearchUniversal twoToneColor={'#FFF'} />}
        data-testid="entities-list-search"
        size="middle"
        placeholder="Search by product name"
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
        }}
        onSearch={() => {
          onSearch(searchTerm);
        }}
        style={{
          width: 300,
        }}
      />
      <CreateEntity
        onCreateClick={onCreateClick}
        onOk={onOk}
        onCancel={onCancel}
      />
    </div>
  );
};

const EntitiesList = ({
  style,
  onCreateClick = () => {},
  onCreateOk = () => {},
  onCreateCancel = () => {},
  onDeleteClick = () => {},
  onDeleteCancel = () => {},
  onDeleteOk = () => {},
  onItemSelect = () => {},
}: {
  style?: React.CSSProperties;
  onCreateClick?: () => void;
  onCreateCancel?: () => void;
  onCreateOk?: () => void;
  onDeleteClick?: () => void;
  onDeleteCancel?: () => void;
  onDeleteOk?: () => void;
  onItemSelect?: () => void;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);

  const offset = (currentPage: number) => (currentPage - 1) * PAGE_SIZE;

  const { data, isLoading } = useGetProductEntities(
    searchTerm,
    offset(currentPage).toString(),
    PAGE_SIZE.toString(),
    {
      staleTime: 1000 * 60 * 5,
    },
  );

  return (
    <Card
      style={style}
      title={
        <EntitiesListTitle
          isLoading={isLoading}
          onSearch={(searchTerm) => {
            setSearchTerm(searchTerm);
            setCurrentPage(1);
          }}
          onCreateClick={onCreateClick}
          onOk={onCreateOk}
          onCancel={onCreateCancel}
        />
      }
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <Entities
        currentPage={currentPage}
        data={data}
        isLoading={isLoading}
        onPageChange={(currentPage: number) => {
          setCurrentPage(currentPage);
        }}
        onDeleteClick={onDeleteClick}
        onCancel={onDeleteCancel}
        onOk={onDeleteOk}
        onItemSelect={onItemSelect}
      />
    </Card>
  );
};

export default EntitiesList;
